import { Button } from "@/components/ui/button";
import { findConnectorApp } from "@/lib/connectors/app-catalog";
import { gatewayOrigin } from "@/lib/gateway-origin";
import { deviceName } from "@/components/devices/device-display";
import {
	createFileRoute,
	Link,
	Navigate,
	useNavigate,
} from "@tanstack/react-router";
import { ConnectionManagement } from "@/components/connectors/connection-management";
import {
	ConnectorMark,
	extractOAuthDomain,
} from "@/components/connectors/connector-display";
import { ConnectorPageShell } from "@/components/connectors/connector-page-shell";
import { ConnectorProvenance } from "@/components/connectors/connector-provenance";
import {
	DETAIL_CRUMB_LINK_CLASS,
	DetailBreadcrumb,
	DetailHeader,
} from "@/components/connectors/detail-header";
import {
	ADD_FEED_DESCRIPTION,
	AddFeedForm,
} from "@/components/entity-tabs/connections-tab/add-feed-form";
import { ConnectionChannelFeed } from "@/components/entity-tabs/connections-tab/connection-channel-feed";
import {
	ConnectionFeedDetail,
	FeedHeaderMeta,
	FeedStatusBadges,
	feedDisplayName,
} from "@/components/entity-tabs/connections-tab/connection-feed-detail";
import { OwnerWorkspaceGate } from "@/components/owner-workspace-gate";
import { RunsLayoutSkeleton } from "@/components/runs/runs-layout-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/ui/status-badge";
import {
	useConnections,
	useConnectorDefinitions,
	useDevices,
	useFeeds,
} from "@/lib/api/connections";
import { useWorkspaceRoot } from "@/lib/api/entities";
import { connectionLabel } from "@/lib/connection-label";
import {
	deviceAppId,
	deviceInWorkspace,
	connectorIsDeviceCapability,
} from "@/lib/connectors/device-rows";
import { connectionDisplayError } from "@/lib/device-pin-tombstone";
import { pruneSearch } from "@/lib/router-search";
import { getStatusVariant } from "@/lib/status-variants";

export const Route = createFileRoute(
	"/$owner/connectors/$connectorKey/$connectionId",
)({
	validateSearch: (search: Record<string, unknown>) =>
		pruneSearch({
			// Replaces ?tab=settings. Settings is a disclosure on the page now, so
			// the deep link opens it rather than selecting a panel.
			settings:
				search.settings === true || search.settings === "true"
					? true
					: undefined,
			auth_result:
				search.auth_result === "connected" ||
				search.auth_result === "cancelled" ||
				search.auth_result === "failed"
					? (search.auth_result as "connected" | "cancelled" | "failed")
					: undefined,
			addFeed:
				search.addFeed === true || search.addFeed === "true" ? true : undefined,
			feedId:
				typeof search.feedId === "number"
					? search.feedId
					: typeof search.feedId === "string" && /^\d+$/.test(search.feedId)
						? Number(search.feedId)
						: undefined,
		}),
	loaderDeps: ({ search: { feedId } }) => ({ feedId }),
	loader: ({ context: { queryClient }, params, deps }) => {
		// Fire-and-forget — workspace + connection prefetches warm the cache
		// but never block paint. ConnectionDetailView renders its skeleton
		// via isWorkspaceLoading until everything resolves. `useFeeds` is
		// only consumed on feedId deep-links so we chain its prefetch behind
		// workspace too.
		void queryClient
			.fetchQuery(
				useWorkspaceRoot.queryOptions(params.owner, { slug: params.owner }),
			)
			.then((ws) => {
				const orgId = ws?.workspace?.id;
				if (!orgId) return;
				void queryClient.prefetchQuery(
					useConnections.queryOptions(orgId, {
						connectorKey: params.connectorKey,
						connectionIds: [Number(params.connectionId)],
					}),
				);
				void queryClient.prefetchQuery(
					useConnectorDefinitions.queryOptions(orgId),
				);
				if (deps.feedId !== undefined) {
					void queryClient.prefetchQuery(
						useFeeds.queryOptions(Number(params.connectionId)),
					);
				}
			})
			.catch(() => {
				// Page handles missing-workspace; swallow loader errors here.
			});
	},
	component: ConnectionDetailRoute,
});

function ConnectionDetailRoute() {
	const { owner, connectorKey, connectionId } = Route.useParams();
	const { addFeed, feedId, settings, auth_result } = Route.useSearch();
	return (
		<OwnerWorkspaceGate owner={owner}>
			{(props) => (
				<ConnectionDetailView
					organizationId={props.organizationId}
					ownerSlug={props.ownerSlug}
					isWorkspaceLoading={props.isWorkspaceLoading}
					connectorKey={connectorKey}
					connectionId={Number(connectionId)}
					showAddFeed={addFeed === true}
					feedId={feedId}
					openSettings={settings === true}
					authResult={auth_result}
				/>
			)}
		</OwnerWorkspaceGate>
	);
}

function ConnectionDetailView({
	organizationId,
	ownerSlug,
	isWorkspaceLoading,
	connectorKey,
	connectionId,
	showAddFeed,
	feedId,
	openSettings,
	authResult,
}: {
	organizationId: string | null;
	ownerSlug: string;
	isWorkspaceLoading: boolean;
	connectorKey: string;
	connectionId: number;
	showAddFeed: boolean;
	feedId: number | undefined;
	openSettings: boolean;
	authResult?: "connected" | "cancelled" | "failed";
}) {
	const navigate = useNavigate();
	const { data: devices = [] } = useDevices();
	const {
		data: connections = [],
		isLoading,
		isError,
		refetch,
	} = useConnections(organizationId ?? "", {
		connectorKey,
		connectionIds: [connectionId],
	});
	const { data: connectorDefs = [] } = useConnectorDefinitions(
		organizationId ?? "",
	);
	const {
		data: feeds = [],
		isLoading: isFeedsLoading,
		isError: isFeedsError,
		refetch: refetchFeeds,
	} = useFeeds(feedId ? connectionId : null);
	const connection = connections.find((c) => c.id === connectionId);
	const connectorDef = connectorDefs.find((d) => d.key === connectorKey);
	const feed = feedId ? feeds.find((f) => f.id === feedId) : null;

	if (
		isWorkspaceLoading ||
		isLoading ||
		(feedId && isFeedsLoading) ||
		!organizationId
	) {
		// Chrome-paint-first: render name + breadcrumb skeletons in the same
		// position the loaded chrome will occupy, plus the two-column
		// runs-left / form-right skeleton for the body, so nothing reflows
		// when data lands.
		return (
			<div className="flex flex-1 flex-col gap-4">
				<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
					<Skeleton className="h-7 w-56" />
					<Skeleton className="h-4 w-24" />
				</div>
				<RunsLayoutSkeleton />
			</div>
		);
	}
	if (isError || (feedId && isFeedsError))
		return (
			<div role="alert" className="space-y-2">
				<p>Could not load this {isError ? "connection" : "feed"}.</p>
				<Button
					variant="outline"
					size="sm"
					onClick={() => void (isError ? refetch() : refetchFeeds())}
				>
					Retry
				</Button>
			</div>
		);
	if (!connection) {
		return (
			<div>
				<p className="text-sm text-muted-foreground">Connection not found.</p>
				<Link
					to="/$owner/connectors/$connectorKey"
					params={{ owner: ownerSlug, connectorKey }}
					className="mt-2 inline-block text-sm text-primary hover:underline"
				>
					Back to {connectorDef?.name ?? connectorKey}
				</Link>
			</div>
		);
	}

	const displayName = connectionLabel({
		...connection,
		connector_name: connection.connector_name ?? connectorDef?.name ?? null,
	});

	// Main pane modes: feed detail > add feed form > the connection itself.
	const isDefaultMode = !feedId && !showAddFeed;

	const goToConnection = () =>
		void navigate({
			to: "/$owner/connectors/$connectorKey/$connectionId",
			params: {
				owner: ownerSlug,
				connectorKey,
				connectionId: String(connectionId),
			},
			search: {},
			replace: true,
		});

	let mainPane: React.ReactNode;
	// In the deeper modes the page is *about* the feed, so the heading names it
	// and the connection moves into the trail. Previously the heading stayed on
	// the connection and the mode leaked into the subtitle as " · Feed" /
	// " · Add feed"; the breadcrumb carries that now.
	let pageTitle = displayName;
	// Feed detail replaces the connection's status/meta with the feed's own.
	let headerStatus: React.ReactNode;
	let headerMeta: React.ReactNode;
	// Deeper modes describe themselves rather than the connection.
	let headerCaption: string | undefined;

	if (feedId) {
		if (!feed) {
			mainPane = (
				<p className="text-sm text-muted-foreground">Feed not found.</p>
			);
			pageTitle = "Feed";
		} else {
			pageTitle = feedDisplayName(feed, connectorDef);
			if (feed.store === "channel_messages") {
				// A chat channel is a channel_messages feed: its detail is the conversation
				// (channel_messages lens), not a runs table. The page shell owns
				// title + breadcrumb back, so the body omits its own chrome.
				mainPane = <ConnectionChannelFeed feed={feed} hideHeader />;
				headerCaption = "chat channel";
			} else {
				// The feed's status/meta ride in the page header rather than a second
				// header inside the pane, so the feed level looks like every other.
				headerStatus = <FeedStatusBadges feed={feed} ownerSlug={ownerSlug} />;
				headerMeta = <FeedHeaderMeta feed={feed} />;
				mainPane = (
					<ConnectionFeedDetail
						feed={feed}
						connectorDef={connectorDef}
						ownerSlug={ownerSlug}
					/>
				);
			}
		}
	} else if (showAddFeed) {
		mainPane = (
			<AddFeedForm
				onClose={goToConnection}
				organizationId={organizationId}
				connections={[connection]}
				connectorDefinitions={connectorDefs}
			/>
		);
		pageTitle = "Add feed";
		headerCaption = ADD_FEED_DESCRIPTION;
	}

	// The trail always names every ancestor, so the deeper modes (feed detail,
	// add feed) show the connection they belong to as a real crumb instead of
	// hiding it behind a linked title.
	const device = devices.find(
		(d) =>
			d.id === connection.device_worker_id && deviceInWorkspace(d, ownerSlug),
	);
	const appId = device ? deviceAppId(device) : undefined;
	const app = appId
		? findConnectorApp(ownerSlug, appId, gatewayOrigin())
		: undefined;
	const crumbs = [
		<Link
			key="connectors"
			to="/$owner/connectors"
			params={{ owner: ownerSlug }}
			className={DETAIL_CRUMB_LINK_CLASS}
		>
			Connectors
		</Link>,
		...(device && app
			? [
					<Link
						key="app"
						to="/$owner/connectors/app/$appId"
						params={{ owner: ownerSlug, appId: app.id }}
						className={DETAIL_CRUMB_LINK_CLASS}
					>
						{app.name}
					</Link>,
					<Link
						key="device"
						to="/$owner/connectors/device/$deviceId"
						params={{ owner: ownerSlug, deviceId: device.id }}
						className={DETAIL_CRUMB_LINK_CLASS}
					>
						{deviceName(device)}
					</Link>,
				]
			: [
					<Link
						key="connector"
						to="/$owner/connectors/$connectorKey"
						params={{ owner: ownerSlug, connectorKey }}
						search={{}}
						className={DETAIL_CRUMB_LINK_CLASS}
					>
						{connectorDef?.name ?? connectorKey}
					</Link>,
				]),
	];
	if (!isDefaultMode) {
		crumbs.push(
			<Link
				key="connection"
				to="/$owner/connectors/$connectorKey/$connectionId"
				params={{
					owner: ownerSlug,
					connectorKey,
					connectionId: String(connectionId),
				}}
				search={{}}
				className={DETAIL_CRUMB_LINK_CLASS}
			>
				{displayName}
			</Link>,
		);
	}

	// Caption: errors always; feed modes may set a short kind line. Never
	// "Discord · BYO" — breadcrumb + title already name the connection.
	const errorText = isDefaultMode ? connectionDisplayError(connection) : null;
	const caption = headerCaption ?? errorText ?? undefined;

	const header = (
		<DetailHeader
			breadcrumb={<DetailBreadcrumb crumbs={crumbs} />}
			icon={
				connectorDef ? (
					<ConnectorMark
						icon={connectorDef.icon}
						name={connectorDef.name}
						faviconDomain={
							connectorDef.favicon_domain ??
							extractOAuthDomain(connectorDef.auth_schema)
						}
					/>
				) : null
			}
			title={pageTitle}
			status={
				headerStatus ?? (
					<StatusBadge status={getStatusVariant(connection.status)} showDot>
						{connection.status}
					</StatusBadge>
				)
			}
			meta={headerMeta}
			caption={caption}
			captionTone={errorText ? "error" : "muted"}
		/>
	);

	// Feed detail / add-feed: the page is about the feed. The breadcrumb carries
	// the way back, so there is no tab bar to keep in sync with it.
	if (!isDefaultMode) {
		return (
			<ConnectorPageShell header={header}>
				<div className="mt-0">{mainPane}</div>
			</ConnectorPageShell>
		);
	}

	if (device && connectorDef && connectorIsDeviceCapability(connectorDef))
		return (
			<Navigate
				to="/$owner/connectors/device/$deviceId"
				params={{ owner: ownerSlug, deviceId: device.id }}
				search={{
					connection: connection.id,
					settings: openSettings || undefined,
				}}
				hash={`connection-${connection.id}`}
				replace
			/>
		);

	return (
		<ConnectorPageShell header={header}>
			<ConnectorProvenance
				connectorDef={connectorDef}
				connectorKey={connectorKey}
				ownerSlug={ownerSlug}
				deviceWorkerId={connection.device_worker_id}
				deviceLabel={connection.device_label}
			/>

			<ConnectionManagement
				embedded
				key={connection.id}
				connection={connection}
				connectorDef={connectorDef}
				ownerSlug={ownerSlug}
				openSettings={openSettings}
				authResult={authResult}
			/>
		</ConnectorPageShell>
	);
}
